import {
  collapseToRanges,
  expandCodeList,
  intersectCodes,
} from "helpers/codeRanges";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Chips } from "primereact/chips";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { RadioButton } from "primereact/radiobutton";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";
import { RulesetApplicability } from "types/DefaultRules/DefaultRules";
import { LesserOfChargesScope } from "types/LesserOfCharges/LesserOfCharges";

import { sanitizeDrgCodes } from "./InpatientRulesetForm";
import {
  groupedRulesAllowed,
  groupedRulesComplete,
  IGroupedRuleRow,
  LesserOfChargesGroupedRules,
} from "./LesserOfChargesGroupedRules";

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FieldHelp = styled.span`
  font-size: 0.85rem;
  color: var(--gray-600);
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const RadioLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const SCOPE_OPTIONS: { label: string; value: LesserOfChargesScope }[] = [
  { label: "Line charges", value: "LINE_CHARGES" },
  { label: "Total charges", value: "TOTAL_CHARGES" },
];

// CLE-2524 applicability choices for ladder rule types.
const APPLICABILITY_OPTIONS: {
  value: RulesetApplicability;
  label: string;
  help: string;
}[] = [
  {
    value: "SCOPED",
    label: "Payer-Provider rule",
    help: "Applies only to the selected provider and payer.",
  },
  {
    value: "TYPICAL",
    label: "Typical rule",
    help: "Template only. No effect until copied into a contract.",
  },
  {
    value: "FALLBACK",
    label: "Fallback rule",
    help: "Live — applies to every provider that has no more specific rule of this type. Optionally limit it to a single payer.",
  },
];

export interface ILesserOfChargesFormData {
  scope: LesserOfChargesScope;
  multiplier: number;
  providerId: string | null;
  payerId: number | null;
  codes: string[];
  excludedCodes: string[];
  groupName: string;
  applicability: RulesetApplicability;
  /** CLE-3058 — Line charges payer-provider rules only. */
  groupedRulesets: IGroupedRuleRow[];
}

export const INITIAL_LESSER_OF_CHARGES_FORM_DATA: ILesserOfChargesFormData = {
  scope: "LINE_CHARGES",
  multiplier: 1,
  providerId: null,
  payerId: null,
  codes: [],
  excludedCodes: [],
  groupName: "",
  applicability: "SCOPED",
  groupedRulesets: [],
};

interface ILesserOfChargesFormProps {
  title: string;
  submitLabel: string;
  formData: ILesserOfChargesFormData;
  onChange: (data: ILesserOfChargesFormData) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  /** CLE-2524: applicability is immutable after create — edit mode locks the radio group. */
  scopeLocked?: boolean;
}

export function LesserOfChargesForm({
  title,
  submitLabel,
  formData,
  onChange,
  onSubmit,
  isSubmitting,
  scopeLocked,
}: ILesserOfChargesFormProps) {
  const navigate = useNavigate();
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();

  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];
    return providers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers]);

  const payerOptions = useMemo(() => {
    const base = (payers?.data ?? []).map((p: any) => ({
      label: p.name,
      value: p.id,
    }));
    return [{ label: "All payers", value: null }, ...base];
  }, [payers]);

  // CLE-2597 — keep the scope fields consistent with the chosen applicability, so a
  // provider left over from SCOPED can't be submitted (and silently dropped) as a fallback.
  const handleApplicabilityChange = (value: RulesetApplicability) => {
    onChange({
      ...formData,
      applicability: value,
      providerId: value === "SCOPED" ? formData.providerId : null,
      payerId: value === "TYPICAL" ? null : formData.payerId,
    });
  };

  const handleChange = <K extends keyof ILesserOfChargesFormData>(
    field: K,
    value: ILesserOfChargesFormData[K]
  ) => {
    onChange({ ...formData, [field]: value });
  };

  const isValid =
    !!formData.scope &&
    (formData.applicability !== "SCOPED" || formData.providerId !== null) &&
    groupedRulesComplete(
      formData.groupedRulesets,
      formData.scope,
      formData.applicability
    ) &&
    !isSubmitting;

  // Surface codes that appear in both lists. By backend design Excluded wins,
  // but listing the same code in both is almost always a mistake — flag it.
  // Expand range chips first so an overlap hidden inside a range is still caught,
  // then collapse the result so the warning reads "0300-0420" not 121 codes.
  const overlappingCodes = useMemo(
    () =>
      collapseToRanges(
        intersectCodes(
          expandCodeList(formData.codes),
          expandCodeList(formData.excludedCodes)
        )
      ),
    [formData.codes, formData.excludedCodes]
  );

  return (
    <div className="p-4">
      <div className="flex align-items-center mb-4">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text mr-2"
          onClick={() => navigate("/payer-rules")}
          aria-label="Back"
        />
        <h2 className="m-0">{title}</h2>
      </div>

      <Message
        severity="info"
        className="mb-4"
        text="This rule applies as a final ceiling — it overrides all other adjustments and inpatient rulesets."
      />

      <Card className="mb-4">
        <FieldGrid>
          <Field>
            <label htmlFor="scope">Scope</label>
            <Dropdown
              inputId="scope"
              value={formData.scope}
              options={SCOPE_OPTIONS}
              onChange={(e) => handleChange("scope", e.value)}
              placeholder="Select scope"
            />
            <FieldHelp>
              Line charges caps each CPT line; Total charges caps the claim
              total.
            </FieldHelp>
          </Field>
          <Field>
            <label htmlFor="multiplier">Multiplier</label>
            <InputNumber
              inputId="multiplier"
              value={formData.multiplier}
              onValueChange={(e) => handleChange("multiplier", e.value ?? 1)}
              mode="decimal"
              minFractionDigits={2}
              maxFractionDigits={4}
              min={0}
              max={99}
            />
            <FieldHelp>
              0.70 = 70% of charges, 1.00 = 100% of charges.
            </FieldHelp>
          </Field>
        </FieldGrid>

        <RadioGroup>
          {APPLICABILITY_OPTIONS.map((option) => (
            <RadioLine key={option.value}>
              <RadioButton
                inputId={`applicability-${option.value}`}
                name="applicability"
                value={option.value}
                checked={formData.applicability === option.value}
                disabled={scopeLocked}
                onChange={() => handleApplicabilityChange(option.value)}
              />
              <label
                htmlFor={`applicability-${option.value}`}
                style={{ fontWeight: 600, cursor: "pointer" }}
              >
                {option.label}
              </label>
              <FieldHelp>{option.help}</FieldHelp>
            </RadioLine>
          ))}
        </RadioGroup>

        {formData.applicability === "SCOPED" && (
          <FieldGrid>
            <Field>
              <label htmlFor="providerId">Provider</label>
              <Dropdown
                inputId="providerId"
                value={formData.providerId}
                options={providerOptions}
                onChange={(e) => handleChange("providerId", e.value ?? null)}
                placeholder="Select a provider"
                filter
                disabled={scopeLocked}
              />
            </Field>
            <Field>
              <label htmlFor="payerId">Payer</label>
              <Dropdown
                inputId="payerId"
                value={formData.payerId}
                options={payerOptions}
                onChange={(e) => handleChange("payerId", e.value ?? null)}
                placeholder="Select a payer"
                filter
                disabled={scopeLocked}
              />
              <FieldHelp>
                Leave as &quot;All payers&quot; to apply this rule to every
                payer for this provider.
              </FieldHelp>
            </Field>
          </FieldGrid>
        )}

        {/* CLE-2597 — a fallback may name one payer: the same floor, every provider. */}
        {formData.applicability === "FALLBACK" && (
          <FieldGrid>
            <Field>
              <label htmlFor="fallbackPayerId">Payer</label>
              <Dropdown
                inputId="fallbackPayerId"
                value={formData.payerId}
                options={payerOptions}
                onChange={(e) => handleChange("payerId", e.value ?? null)}
                placeholder="All payers"
                filter
                disabled={scopeLocked}
              />
              <FieldHelp>
                Leave as &quot;All payers&quot; for the global fallback. Pick a
                payer to floor that payer across every provider — a rule written
                for a specific provider still wins over this.
              </FieldHelp>
            </Field>
          </FieldGrid>
        )}

        <FieldGrid>
          <Field>
            <label htmlFor="codes">Codes</label>
            <Chips
              inputId="codes"
              value={formData.codes}
              onChange={(e) =>
                handleChange("codes", sanitizeDrgCodes(e.value ?? []))
              }
              placeholder="96523, J1642, 470, 0272-0372"
              separator=","
            />
            <FieldHelp>
              Enter individual codes (96523, J1642) or ranges (0272-0372). The
              rule applies to any claim that contains at least one of these
              codes. Leave empty to apply to every claim.
            </FieldHelp>
          </Field>
          <Field>
            <label htmlFor="excludedCodes">Excluded Codes</label>
            <Chips
              inputId="excludedCodes"
              value={formData.excludedCodes}
              onChange={(e) =>
                handleChange("excludedCodes", sanitizeDrgCodes(e.value ?? []))
              }
              placeholder="99213, 470"
              separator=","
            />
            <FieldHelp>
              Codes to skip from the rule. If a code here matches the claim at
              the header level, the rule does not apply at all. Service lines
              whose code matches one here are dropped from the cap calculation.
            </FieldHelp>
          </Field>
        </FieldGrid>

        {overlappingCodes.length > 0 && (
          <Message
            severity="warn"
            className="mb-4"
            text={`Code${
              overlappingCodes.length > 1 ? "s" : ""
            } ${overlappingCodes.join(", ")} appear${
              overlappingCodes.length === 1 ? "s" : ""
            } in both lists — Excluded Codes will win.`}
          />
        )}

        {/* CLE-3058 — grouped rules: Line charges payer-provider rules only. */}
        {groupedRulesAllowed(formData.scope, formData.applicability) && (
          <LesserOfChargesGroupedRules
            rows={formData.groupedRulesets}
            providerId={formData.providerId}
            payerId={formData.payerId}
            onChange={(rows) => handleChange("groupedRulesets", rows)}
          />
        )}

        <FieldGrid>
          <Field>
            <label htmlFor="groupName">Description</label>
            <InputText
              id="groupName"
              value={formData.groupName}
              onChange={(e) => handleChange("groupName", e.target.value)}
              placeholder="Optional label shown in pricing breakdowns"
            />
          </Field>
        </FieldGrid>

        <Footer>
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => navigate("/payer-rules")}
          />
          <Button
            label={submitLabel}
            icon="pi pi-check"
            onClick={onSubmit}
            disabled={!isValid}
            loading={isSubmitting}
          />
        </Footer>
      </Card>
    </div>
  );
}
